import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Copy, Check, ArrowLeft, MapPin, Eye, Loader2, Download } from 'lucide-react';
import { toPng } from 'html-to-image';
import { shareGet } from '@/lib/api';
import { toast } from 'sonner';

export default function ShareView() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    let alive = true;
    shareGet(sid).then(d => alive && setData(d)).catch(() => alive && setErr(true)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [sid]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };

  const exportImage = async () => {
    if (!cardRef.current || exporting) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true, pixelRatio: 2,
        backgroundColor: '#F2F0E4',
        skipFonts: true,
      });
      // Try Web Share API (mobile)
      try {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], `trivly-${sid}.png`, { type: 'image/png' });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: data?.title || 'My trip plan', text: 'Made with Trivly' });
          toast.success('Card shared');
          return;
        }
      } catch {}
      // Fallback to download
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `trivly-${sid}.png`;
      a.click();
      toast.success('Image saved');
    } catch (e) {
      toast.error('Could not export image');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="share-view-screen" className="min-h-dvh">
      <div className="relative">
        <div className="absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-[#C4FF3D]/40 via-[#C4FF3D]/10 to-transparent pointer-events-none" />
        <div className="relative px-5 pt-5 pb-2 flex items-center justify-between">
          <button type="button" onClick={() => navigate('/')} className="w-10 h-10 rounded-full bg-[#0F1A13]/05 border border-[#0F1A13]/10 flex items-center justify-center text-[#0F1A13] gt-focus" aria-label="Home" data-testid="share-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="px-3 py-1.5 rounded-full bg-[#15291C] text-[#C4FF3D] text-[11px] font-bold flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Shared by Trivly
          </span>
        </div>
      </div>

      <div className="px-5 pt-3 pb-6">
        {loading && (
          <div className="glass-card p-6 flex items-center gap-3"><Loader2 className="w-4 h-4 animate-spin text-[#15291C]" /><span className="text-[#4A5A50] text-sm">Loading itinerary…</span></div>
        )}
        {err && (
          <div className="glass-card p-5 text-[#4A5A50] text-sm" data-testid="share-error">
            This itinerary doesn't exist or has been removed.
          </div>
        )}
        {data && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            {/* Snapshot region — also what gets exported as a Trip Card PNG */}
            <div ref={cardRef} className="bg-[#F2F0E4] rounded-3xl p-4 -mx-1" data-testid="share-card">
              <h1 className="font-display text-3xl font-bold tracking-tight text-[#0F1A13]" data-testid="share-title">{data.title}</h1>
              <div className="mt-2 flex items-center gap-3 text-[12px] text-[#4A5A50]">
                {(data.city_name || data.country) && (
                  <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#15291C]" /> {[data.city_name, data.country].filter(Boolean).join(', ')}</span>
                )}
                <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> {data.views} {data.views === 1 ? 'view' : 'views'}</span>
              </div>

              <div className="glass-card p-5 mt-5">
                <p className="text-[#0F1A13] text-[14px] leading-relaxed whitespace-pre-wrap" data-testid="share-body">{data.body}</p>
              </div>

              {/* In-card watermark (so the exported PNG always carries it) */}
              <div className="mt-4 flex items-center justify-center gap-2 pb-1" data-testid="share-card-watermark">
                <span className="w-6 h-6 rounded-md overflow-hidden">
                  <img src="/brand/trivly-app-icon.png" alt="" className="w-full h-full object-cover" draggable={false} />
                </span>
                <span className="text-[11.5px] text-[#15291C] font-semibold tracking-wide">
                  Made with Trivly · trivly.ai
                </span>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <button type="button" onClick={exportImage} disabled={exporting} className="gt-btn-secondary flex items-center justify-center gap-2 disabled:opacity-60" data-testid="share-save-image">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin text-[#15291C]" /> : <Download className="w-4 h-4 text-[#15291C]" />}
                {exporting ? 'Building card…' : 'Save / share as image'}
              </button>
              <button type="button" onClick={copyLink} className="gt-btn-secondary flex items-center justify-center gap-2" data-testid="share-copy-link">
                {copied ? <Check className="w-4 h-4 text-[#15291C]" /> : <Copy className="w-4 h-4 text-[#15291C]" />}
                {copied ? 'Link copied' : 'Copy link'}
              </button>
              <button type="button" onClick={() => navigate('/onboarding')} className="gt-btn-primary flex items-center justify-center gap-2" data-testid="share-try-cta">
                <Sparkles className="w-4 h-4" /> Try Trivly free
              </button>
            </div>

            {/* Trivly wordmark watermark — subtle branding for shared pages */}
            <div className="mt-8 flex flex-col items-center gap-2 pb-4" data-testid="share-watermark">
              <a
                href="https://trivly.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 group hover:opacity-100 opacity-90 transition-opacity"
                data-testid="share-watermark-link"
              >
                <span className="w-7 h-7 rounded-lg overflow-hidden shadow-[0_4px_10px_rgba(15,26,19,0.15)] group-hover:shadow-[0_4px_14px_rgba(196,255,61,0.45)] transition-shadow">
                  <img src="/brand/trivly-app-icon.png" alt="" className="w-full h-full object-cover" draggable={false} />
                </span>
                <p className="text-[11px] text-[#7A8A80] tracking-wide group-hover:text-[#15291C] transition-colors">
                  Made with <span className="font-bold text-[#15291C]">Trivly</span> · trivly.ai
                </p>
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
